import { useState } from 'react';
import { X, Mail, User, Send, Loader2 } from 'lucide-react';
import { ProjectData } from '../types';
import { environment } from '../config/environment';

interface CustomerUpdateModalProps {
  project: ProjectData;
  isOpen: boolean;
  onClose: () => void;
  onUpdate: () => void;
}

export function CustomerUpdateModal({ project, isOpen, onClose, onUpdate }: CustomerUpdateModalProps) {
  const [customerName, setCustomerName] = useState(project.customerName);
  const [customerEmail, setCustomerEmail] = useState(project.customerEmail);
  const [resendEmail, setResendEmail] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsUpdating(true);
    setError(null);
    setSuccess(null);

    try {
      // Use environment configuration to determine the correct Cloud Function URL
      const functionsUrl = environment.useEmulators 
        ? `http://127.0.0.1:5001/ima-onboard/us-central1/updateCustomerInfo`
        : `https://us-central1-ima-onboard.cloudfunctions.net/updateCustomerInfo`;
      
      const response = await fetch(functionsUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          jiraKey: project.jiraKey,
          customerName,
          customerEmail,
          resendEmail
        }),
      });

      const result = await response.json();

      if (response.ok && result.success) {
        setSuccess(`Customer information updated successfully${result.data.emailSent ? ' and welcome email sent' : ''}`);
        onUpdate(); // Refresh the project data
        setTimeout(() => {
          onClose();
        }, 2000);
      } else {
        setError(result.error || 'Failed to update customer information');
      }
    } catch (error) {
      console.error('Error updating customer information:', error);
      setError('Failed to update customer information. Please try again.');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleClose = () => {
    if (!isUpdating) {
      setError(null);
      setSuccess(null);
      setResendEmail(false);
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <User className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Update Customer Information</h3>
              <p className="text-sm text-gray-500">Project: {project.jiraKey}</p>
            </div>
          </div>
          <button
            onClick={handleClose}
            disabled={isUpdating}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
          >
            <X className="h-5 w-5 text-gray-400" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Customer Name */}
          <div>
            <label htmlFor="customerName" className="block text-sm font-medium text-gray-700 mb-2">
              <User className="h-4 w-4 inline mr-1" />
              Customer Name
            </label>
            <input
              type="text"
              id="customerName"
              value={customerName}
              onChange={(e) => setCustomerName(e.target.value)}
              required
              disabled={isUpdating}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
              placeholder="Enter customer name"
            />
          </div>

          {/* Customer Email */}
          <div>
            <label htmlFor="customerEmail" className="block text-sm font-medium text-gray-700 mb-2">
              <Mail className="h-4 w-4 inline mr-1" />
              Customer Email
            </label>
            <input
              type="email"
              id="customerEmail"
              value={customerEmail}
              onChange={(e) => setCustomerEmail(e.target.value)}
              required
              disabled={isUpdating}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
              placeholder="Enter customer email"
            />
          </div>

          {/* Resend Email Option */}
          <div className="flex items-center space-x-3">
            <input
              type="checkbox"
              id="resendEmail"
              checked={resendEmail}
              onChange={(e) => setResendEmail(e.target.checked)}
              disabled={isUpdating}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded disabled:opacity-50"
            />
            <label htmlFor="resendEmail" className="text-sm text-gray-700">
              Resend welcome email to updated address
            </label>
          </div>

          {/* Error Message */}
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          {/* Success Message */}
          {success && (
            <div className="p-3 bg-green-50 border border-green-200 rounded-md">
              <p className="text-sm text-green-600">{success}</p>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={handleClose}
              disabled={isUpdating}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isUpdating || !customerName.trim() || !customerEmail.trim()}
              className="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isUpdating ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span>Updating...</span>
                </>
              ) : (
                <>
                  <Send className="h-4 w-4" />
                  <span>Update Customer</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
